/**
 * Widget design tokens — slot geometry, pills, density (§6g).
 *
 * The single accessor module for the muiTheme design-token surface. Every
 * slot/control call site (PureListView rows, CondensedView TimeslotButton,
 * FullCalendar CSS, DomHandler scope container, defaults.jsx overrides) reads
 * its geometry through these accessors so caps and unit discipline live in
 * exactly one place.
 *
 * LEAF-MODULE RULE: this file imports NOTHING from `src/`. It is the sink of a
 * strict DAG (`defaults.jsx` → tokens; `fullcalendar-css.js` → tokens; views →
 * tokens). Do not add `src/` imports here.
 *
 * CONTRACT:
 * - Return type is a px NUMBER, or `null` when the token is unset. Call sites
 *   own their own (often context-dependent) fallbacks via `?? current`; only
 *   `slotRadius`/`controlRadius` embed their fallback (`shape.borderRadius`,
 *   always on the theme) and `slotGap` embeds `8`.
 * - "Set" means NON-NULL (`!= null`). `0` is a valid token value and must be
 *   honoured. NEVER use presence (`'key' in obj`) checks — a hand-written
 *   `slotRadius: null` must read as unset, not as `0`.
 * - Caps live here and match the editor yup schema (`slotPaddingX` ≤ 20) so the
 *   editor can never accept a value the widget silently clamps.
 */

/**
 * Slot corner radius (list rows, timeslot buttons, slot skeletons).
 * Falls back to the theme's global border radius when unset.
 * @param {object} theme
 * @returns {number} px
 */
export function slotRadius(theme) {
  const r = theme.shape?.slotRadius;
  return r != null ? r : theme.shape?.borderRadius ?? 6;
}

/**
 * Control corner radius (buttons, button groups, day-number chip, week tabs).
 * Falls back to the theme's global border radius when unset.
 * @param {object} theme
 * @returns {number} px
 */
export function controlRadius(theme) {
  const r = theme.shape?.controlRadius;
  return r != null ? r : theme.shape?.borderRadius ?? 6;
}

/**
 * Vertical slot padding. Raw token, or `null` when unset (caller keeps its own
 * context-dependent default).
 *
 * CALIBRATION: the token names the value for a PureListView row, whose unset
 * baseline is `py: 2` (16px large container). Every other consumer scales that
 * canonical number onto its own baseline rather than applying it raw — the list
 * halves it below 360px, and CondensedView divides it down to the MUI Button
 * variant padding it inherits when unset. Skipping that step is what made
 * ThemeEditor's Dichte "Kompakt" render TALLER than the unset "Standard" in the condensed view.
 * @param {object} theme
 * @returns {number|null} px or null
 */
export function slotPaddingY(theme) {
  const v = theme.layout?.slotPaddingY;
  // Capped to match the ThemeEditor's yup max, so no theme can hand a call site a
  // value the editor would have rejected (the slotPaddingX rule, applied here
  // too).
  return v != null ? Math.min(v, 32) : null;
}

/**
 * The `slotPaddingY` value that means "render what an unset theme renders".
 * It is the Dichte control's own Standard driver value, and every consumer
 * calibrates against it: consumer_baseline_px = token × (consumer_unset_px / 15).
 */
export const CANONICAL_SLOT_PADDING_Y = 15;

/**
 * Horizontal slot padding, capped at 20 (= the editor yup max) so no
 * permutation can overflow the 120px minimum condensed column. `null` when
 * unset.
 * @param {object} theme
 * @returns {number|null} px or null
 */
export function slotPaddingX(theme) {
  const v = theme.layout?.slotPaddingX;
  return v != null ? Math.min(v, 20) : null;
}

/**
 * Vertical gap between slots (list `marginBottom`, condensed `rowSpacing`,
 * skeleton row gaps). Falls back to 8px when unset.
 * @param {object} theme
 * @returns {number} px
 */
export function slotGap(theme) {
  const v = theme.layout?.slotGap;
  return v != null ? v : 8;
}

/**
 * Outer card padding for the widget scope container (third-party embeds only;
 * hosted pages let the chrome own padding). `null` when unset.
 * @param {object} theme
 * @returns {number|null} px or null
 */
export function cardPadding(theme) {
  const v = theme.layout?.cardPadding;
  return v != null ? v : null;
}

/**
 * FullCalendar event radius. Capped at 12 ONLY when `slotRadius` is explicitly
 * set — because the event rule paints a 4px `::before` accent bar under
 * `overflow: hidden`, so a fully-round radius would clip the bar. When
 * `slotRadius` is unset the value falls through UNCAPPED to the global border
 * radius, keeping every existing `borderRadius > 12` theme byte-identical.
 * The value-check (`!= null`, never `'slotRadius' in shape`) matters: a
 * hand-written `slotRadius: null` must not `Math.min(null, 12)` into a square 0.
 * @param {object} theme
 * @returns {number} px
 */
export function fcEventRadius(theme) {
  const r = theme.shape?.slotRadius;
  return r != null ? Math.min(r, 12) : theme.shape?.borderRadius ?? 6;
}
